import type { ParseJSONObject } from '@ikegps/core-api';
import type { Log } from 'bblog';
import { IkeArray } from '../util/array';
import type { IkeRequestError } from './rest.error';

export class IkeFetchableStore<T> {
	error: IkeRequestError | null = null;
	promise: Promise<T> | null = null;
	isFetching: boolean = false;
	value?: T;
	lastFetch: number;

	setIsFetching(isFetching: boolean) {
		this.isFetching = isFetching;
	}
	setValue(value: T, lastFetch: number) {
		this.value = value;
		this.lastFetch = lastFetch;
		this.isFetching = false;
	}
	setError(error: IkeRequestError) {
		this.error = error;
		this.isFetching = false;
	}
}

/**
 * A lazy loaded valued value.
 *
 * This will only make a HTTP request when the value is not loaded.
 * It will also preserve any errors resulting from the fetch
 */
export interface IkeFetchable<T> {
	/** Date the last time this was fetched */
	lastFetch?: number;
	/** Is there a outstanding fetch request */
	isFetching: boolean;
	/** Has this been fetched before */
	hasFetched: boolean;
	/**
	 * Current cached value.
	 * If the fetchable hasn't fetched yet, this will trigger a fetch.
	 *
	 * @returns Value if it has been fetched, null otherwise
	 * @deprecated Can cause race conditions. Use `await fetch()` instead.
	 */
	value: T | null;
	/** Last error caused by the fetch */
	error: IkeRequestError | null;
	/**
	 * Force a refetch
	 *
	 * @returns Promise of the fetch result
	 **/
	refetch(): Promise<T>;
	/**
	 * Fetch the value, if it has already been fetched return the cached value
	 * @returns Promise of the fetch result
	 */
	fetch(): Promise<T>;
	/** Clear the cache */
	reset(): void;
	/** Set the initial value, so a fetch will not be needed */
	init(obj: T): void;
}

export abstract class IkeFetchableBase<T> implements IkeFetchable<T> {
	protected store: IkeFetchableStore<T>;
	protected query: () => Promise<T>;

	constructor(
		protected log: Log,
		store: IkeFetchableStore<T>,
		query: () => Promise<T>
	) {
		this.query = query;
		this.store = store;
	}

	get hasFetched(): boolean {
		return this.store.lastFetch != null;
	}

	get isFetching(): boolean {
		return this.store.isFetching;
	}

	get lastFetch(): number {
		return this.store.lastFetch;
	}

	get error(): IkeRequestError | null {
		return this.store.error;
	}

	/**
	 * @deprecated Using this getter incorrectly creates race conditions.
	 * Use async {@link fetch} instead, or check {@link isFetching} and {@link error} in a loop until the fetch completes.
	 */
	get value(): T | null {
		// If a previous fetch errored, return null without re-firing the promise.
		// `setError` does not set `lastFetch` and resets `isFetching`, so without this
		// guard each `.value` access would re-enter `fetch()`, return the cached
		// rejected promise, and re-log via the catch below — spamming on every render.
		if (this.store.error != null) {
			return null;
		}

		if (this.hasFetched) {
			return this.store.value ?? null;
		}

		if (!this.isFetching) {
			this.fetch().catch((error) => {
				// We must catch and log here, otherwise the uncaught exception may crash the server.

				this.log.error({ error }, 'Fetchable: caught error in value getter');
			});
		}
		return this.store.value ?? null;
	}

	/**
	 * Re-query the fetchable
	 */
	refetch(): Promise<T> {
		this.store.promise = null;
		return this.fetch();
	}

	/**
	 * Clear the fetchable value.
	 * The fetchable will be re-queried next time it is used.
	 */
	reset(): void {
		this.store.promise = null;
		this.store.setValue(null, null);
		this.store.setError(null);
	}

	async fetch(): Promise<T> {
		if (this.store.promise != null) {
			return await this.store.promise;
		}

		this.store.promise = this.query()
			.then((obj) => {
				this.init(obj);
				return obj;
			})
			.catch((e) => {
				this.store.setError(e);
				throw e;
			});

		this.store.setIsFetching(true);

		return await this.store.promise;
	}

	abstract init(value: T | null): void;
}

export class IkeFetchableMany<T extends ParseJSONObject> extends IkeFetchableBase<T[]> {
	private mapByKey: { [key in keyof T]?: { [value: string]: T } } = {};

	get first(): T | null {
		const value = this.value;
		if (value != null) {
			return value[0];
		}
		return null;
	}

	find<K extends keyof T>(key: K, value: T[K]): T | null {
		if (this.value == null) {
			return null;
		}

		if (typeof value !== 'string') {
			throw new Error(`Unable to lookup by value which is not a string typeof:${typeof value}`);
		}

		let map = this.mapByKey[key];
		if (map == null) {
			map = this.mapByKey[key] = IkeArray.toMap(this.value, key);
		}

		// TODO is there a better way to fix this
		return (map as any)[value];
	}

	/**
	 * Set a initial value for this fetchable,
	 * Last fetch time will use the updatedAt of the objector
	 * the current time depending on which is newer.
	 */
	init(value: T[] | null) {
		this.mapByKey = {};
		this.store.setValue(value, this.lastUpdatedAt(value));
		this.store.promise = Promise.resolve(this.store.value);
		this.store.setError(null);
	}

	lastUpdatedAt(value: T[]) {
		if (value == null || value.length === 0) {
			return Date.now();
		}
		let lastObj: T = null;
		for (const v of value) {
			if (lastObj == null || lastObj.updatedAt < v.updatedAt) {
				lastObj = v;
			}
		}

		return lastFetchObjectOrNow(lastObj);
	}
}

/**
 * Last fetch should be set to the current time,
 * but sometimes user's clocks are off, so use the server time if that
 * is ahead other wise use the user's time
 */
export function lastFetchObjectOrNow(obj: ParseJSONObject): number {
	const currentTime = Date.now();
	if (obj == null || obj.updatedAt == null) {
		return currentTime;
	}

	const updatedAt = new Date(obj.updatedAt).getTime();
	const timeDiff = updatedAt - currentTime;
	if (timeDiff > 0) {
		return updatedAt;
	}

	return currentTime;
}

export class IkeFetchableOne<T extends ParseJSONObject> extends IkeFetchableBase<T> {
	/**
	 * Set a initial value for this fetchable
	 * Last fetch time will use the updatedAt of the object or
	 * the current time depending on which is newer.
	 */
	init(value: T | null) {
		this.store.setValue(value, lastFetchObjectOrNow(value));
		this.store.promise = Promise.resolve(this.store.value);
		this.store.setError(null);
	}
}

export class IkeFetchableTransform<T extends ParseJSONObject, O> extends IkeFetchableBase<O> {
	base: IkeFetchable<T>;
	transform: (obj: T) => Promise<O>;

	constructor(log: Log, store: IkeFetchableStore<O>, base: IkeFetchable<T>, transform: (obj: T) => Promise<O>) {
		super(log, store, () => base.fetch().then((obj) => this.transform(obj)));

		this.base = base;
		this.transform = transform;
	}

	init(value: O | null) {
		this.store.setValue(value, Date.now());
		this.store.promise = Promise.resolve(this.store.value);
		this.store.setError(null);
	}
}

/**
 * Thrown when an access to IkeFetchable.value fails due to a race condition.
 */
export class IkeFetchableRaceError extends Error {}
